import AttachablePanel from "@/lib/printess/attachablePanel";
import Palette from "@/lib/printess/palette/palette_component";
import React from "react";
import * as ReactDOMClient from "react-dom/client";
import {
  PaletteItemsFromPrintessAndSourceMenuItems,
  getItemsFromSourceMenu,
  updateField,
  hasMetaField,
  ensureMetaFieldPresent,
} from "../../../lib/printess/utils";

export const MagicMenuPaletteMixin = (Base) =>
  class extends Base {
    #assortmentPanelContainer;
    #assortmentPanel;
    #assortmentPanelRoot; // React 18 root instance
    #itemsFromSourceMenu;
    #currentFieldName;
    #currentFieldId;
    #currentSelectionProperties = [];
    #sourceMenuEnabled = false;
    #paletteTriggeredByButton = false;

    onEditorLoaded() {
      super.onEditorLoaded();
      // This element will not exist if there is no source menu for the user.
      const sourceMenuElement = document.getElementById("source_menu_palette");

      if (!sourceMenuElement) return;
      this.#sourceMenuEnabled = true;
      const rawSourceMenu = JSON.parse(sourceMenuElement.dataset.sourceMenu);
      this.#itemsFromSourceMenu = getItemsFromSourceMenu(rawSourceMenu);
      ensureMetaFieldPresent(this.#itemsFromSourceMenu);

      this.#initPanel();

      return;
    }

    // Called from the printess Bridge
    async openPalettePanel(buttonName) {
      let formFieldName = buttonName.replace("SourceMenuButton", "");

      if (!this.#sourceMenuEnabled) {
        return;
      }
      const field = this.#getPaletteFieldFromProperties(formFieldName);
      if (!field) {
        return;
      }

      if (this.#currentFieldId && this.#currentFieldId === field.id) {
        return;
      }

      if (this.#currentFieldId && this.#currentFieldId !== field.id) {
        this.#handleHidePaletteTable();
      }

      this.#currentFieldName = formFieldName;
      this.#currentFieldId = field.id;
      this.#paletteTriggeredByButton = true;
      this.#handleShowPaletteTable(field);
    }

    closePalettePanel() {
      const field = this.#getPaletteFieldFromProperties(this.#currentFieldName);

      this.#handleHidePaletteTable(field);
      this.#currentFieldName = null;
      this.#currentFieldId = null;
      this.#paletteTriggeredByButton = false;
    }

    onSelectionChangedRenderPalette(properties) {
      this.#currentSelectionProperties = properties;

      if (!this.#sourceMenuEnabled) {
        return;
      }
      const field = this.#getPaletteFieldFromProperties();
      if (!field) {
        if (this.#currentFieldId) {
          this.#handleHidePaletteTable();
          this.#currentFieldId = null;
        }
        return;
      }

      if (this.#currentFieldId && this.#currentFieldId === field.id) {
        return;
      }

      if (this.#currentFieldId && this.#currentFieldId !== field.id) {
        this.#handleHidePaletteTable();
      }

      this.#currentFieldId = field.id;
      this.#handleShowPaletteTable(field);
    }
    // TODO, handle use case, no form field provided for button
    #getPaletteFieldFromProperties(name = null) {
      let properties = this.#currentSelectionProperties;

      if (!name) {
        const fields = properties.filter(
          (property) =>
            property.classNames &&
            property.classNames.includes("palette_source"),
        );
        if (fields.length === 0) {
          return;
        }
        if (fields.length > 1) {
          throw new Error(
            "Multiple palette source fields found in properties. This is not supported.",
          );
        }

        return fields[0];
      } else {
        return properties.find((property) => property.ffName === name);
      }
    }

    // This is only called the first time for each palette when that palette gets shown.
    #buildPalette(printessField) {
      let printessItems = JSON.parse(printessField.value) ?? {};
      const name = printessField.ffName;

      const handleItemsChanged = (items) => {
        updateField(this.editor.api, name, JSON.stringify(items));
      };

      const paletteItems = hasMetaField(printessItems)
      ?  PaletteItemsFromPrintessAndSourceMenuItems(printessItems, this.#itemsFromSourceMenu)
      : structuredClone(this.#itemsFromSourceMenu);

      // Return config, not a React element
      return { paletteItems, handleItemsChanged };
    }

    async #handleShowPaletteTable(field) {
      let paletteConfig = this.#buildPalette(field);

      this.#assortmentPanelRoot = ReactDOMClient.createRoot(
        this.#assortmentPanelContainer,
      );

      this.#assortmentPanelRoot.render(
        React.createElement(Palette, {
          paletteItems: paletteConfig.paletteItems,
          onItemsChanged: paletteConfig.handleItemsChanged,
        }),
      );
      this.#assortmentPanel.show();
    }

    #handleHidePaletteTable() {
      if (this.#assortmentPanelRoot) {
        this.#assortmentPanelRoot.unmount();
        this.#assortmentPanelRoot = null;
      }
      this.#assortmentPanel.hide();
    }

    #initPanel() {
      this.#assortmentPanelContainer = document.createElement("div");
      document.body.appendChild(this.#assortmentPanelContainer);

      this.#assortmentPanel = new AttachablePanel(
        this.editor,
        this.#assortmentPanelContainer,
        {
          hideNextButton: false,
          showCloseButton: true,
          onClose: this.closePalettePanel.bind(this),
        },
      );
      this.#assortmentPanel.attach();
      this.#assortmentPanelRoot = null;
    }
  };
